import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  TextField,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import {
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { useToast } from '../../contexts/ToastContext';
import { useAdmin } from '../../contexts/AdminContext';
import { getWritingAnswers, deleteWritingAnswer, updateWritingAnswer, getPartnerAdminWritingAnswers, updatePartnerAdminWritingAnswer } from '../../api/answersApi';
import { useGroupedData } from '../../hooks';
import {
  PageHeader,
  StatsCards,
  GroupedAccordionList,
  ModernButton,
} from '../../components/common';
import { format } from 'date-fns';
import { Document, Paragraph, TextRun, HeadingLevel, AlignmentType, Packer } from 'docx';
import { saveAs } from 'file-saver';

const WritingAnswersManagement = () => {
  const { showToast } = useToast();
  const { isPlatformAdmin } = useAdmin();
  const [answers, setAnswers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editedFeedback, setEditedFeedback] = useState({});

  // Use the grouped data hook
  const {
    groupsArray,
    isGroupExpanded,
    toggleGroup,
  } = useGroupedData({
    data: answers,
    getGroupKey: (answer) => answer.userProductRegistrationId?.productId?._id || 'unknown',
    getGroupMeta: (answer) => ({
      examName: answer.userProductRegistrationId?.productId?.name || answer.userProductRegistrationId?.productId?.label || 'Unknown Exam',
      examDate: answer.userProductRegistrationId?.productId?.startDate,
    }),
    itemsKey: 'answers',
  });

  // Fetch data
  const fetchAnswers = useCallback(async () => {
    try {
      setLoading(true);
      // Use partner-scoped API for non-platform admins
      const { data } = isPlatformAdmin
        ? await getWritingAnswers()
        : await getPartnerAdminWritingAnswers();
      setAnswers(data.data || data || []);
    } catch (error) {
      console.error('Failed to fetch writing answers:', error);
      showToast('Failed to load writing answers', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast, isPlatformAdmin]);

  useEffect(() => {
    fetchAnswers();
  }, [fetchAnswers]);

  // Helper functions
  const getStudentName = (answer) => {
    const user = answer.userProductRegistrationId?.userId;
    if (user?.firstName || user?.lastName) {
      return `${user.firstName || ''} ${user.lastName || ''}`.trim();
    }
    if (user?.name) return user.name;
    return 'N/A';
  };

  const getStudentEmail = (answer) => {
    return answer.userProductRegistrationId?.userId?.email || 'N/A';
  };

  const getAnsweredCount = (answerList) => {
    return answerList.filter((a) => a.answer !== null && a.answer !== '').length;
  };

  const hasFeedback = (answerList) => {
    return answerList.some((a) => a.instructorFeedback || a.instructorAdvice);
  };

  // Stats calculation
  const stats = [
    {
      label: 'Total Submissions',
      value: answers.length,
      color: 'warning.main',
    },
    {
      label: 'With Feedback',
      value: answers.filter((a) => hasFeedback(a.answers)).length,
      color: 'success.main',
    },
    {
      label: 'Pending Review',
      value: answers.filter((a) => !hasFeedback(a.answers)).length,
      color: 'error.main',
    },
  ];

  // Dialog handlers
  const handleViewDetails = (answer) => {
    setSelectedAnswer(answer);
    setEditedFeedback({});
    setEditMode(false);
    setDialogOpen(true);
  };

  const handleEditFeedback = () => {
    const feedback = {};
    selectedAnswer.answers.forEach((ans) => {
      feedback[ans.questionNumber] = {
        instructorFeedback: ans.instructorFeedback || '',
        instructorAdvice: ans.instructorAdvice || '',
      };
    });
    setEditedFeedback(feedback);
    setEditMode(true);
  };

  const handleSaveFeedback = async () => {
    try {
      const updatedAnswers = selectedAnswer.answers.map((ans) => ({
        ...ans,
        instructorFeedback: editedFeedback[ans.questionNumber]?.instructorFeedback || ans.instructorFeedback,
        instructorAdvice: editedFeedback[ans.questionNumber]?.instructorAdvice || ans.instructorAdvice,
      }));

      if (isPlatformAdmin) {
        await updateWritingAnswer(selectedAnswer._id, { answers: updatedAnswers });
      } else {
        const feedbackPayload = updatedAnswers.map(({ questionNumber, instructorFeedback, instructorAdvice }) => ({
          questionNumber,
          instructorFeedback,
          instructorAdvice,
        }));
        await updatePartnerAdminWritingAnswer(selectedAnswer._id, { answers: feedbackPayload });
      }
      showToast('Feedback updated successfully', 'success');
      setEditMode(false);
      fetchAnswers();
      setDialogOpen(false);
    } catch (error) {
      console.error('Failed to update feedback:', error);
      showToast('Failed to update feedback', 'error');
    }
  };

  const handleFeedbackChange = (questionNumber, field, value) => {
    setEditedFeedback((prev) => ({
      ...prev,
      [questionNumber]: {
        ...prev[questionNumber],
        [field]: value,
      },
    }));
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this answer record?')) {
      try {
        await deleteWritingAnswer(id);
        showToast('Answer record deleted successfully', 'success');
        fetchAnswers();
      } catch (error) {
        console.error('Failed to delete answer:', error);
        showToast('Failed to delete answer record', 'error');
      }
    }
  };

  // Export to Word
  const exportToWord = async (answer) => {
    try {
      const studentName = getStudentName(answer);
      const studentEmail = getStudentEmail(answer);
      const examName = answer.userProductRegistrationId?.productId?.name || answer.userProductRegistrationId?.productId?.label || 'N/A';
      const examDate = answer.userProductRegistrationId?.productId?.startDate
        ? format(new Date(answer.userProductRegistrationId.productId.startDate), 'dd-MM-yyyy HH:mm')
        : 'N/A';

      const doc = new Document({
        sections: [
          {
            properties: {},
            children: [
              new Paragraph({
                text: 'IELTS Writing Answers',
                heading: HeadingLevel.HEADING_1,
                alignment: AlignmentType.CENTER,
                spacing: { after: 400 },
              }),
              new Paragraph({
                text: 'Student Details',
                heading: HeadingLevel.HEADING_2,
                spacing: { before: 200, after: 200 },
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: 'Name: ', bold: true }),
                  new TextRun(studentName),
                ],
                spacing: { after: 100 },
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: 'Email: ', bold: true }),
                  new TextRun(studentEmail),
                ],
                spacing: { after: 100 },
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: 'Exam: ', bold: true }),
                  new TextRun(examName),
                ],
                spacing: { after: 100 },
              }),
              new Paragraph({
                children: [
                  new TextRun({ text: 'Exam Date: ', bold: true }),
                  new TextRun(examDate),
                ],
                spacing: { after: 400 },
              }),
              ...answer.answers.flatMap((ans) => {
                const taskParagraphs = [
                  new Paragraph({
                    text: `Task ${ans.questionNumber}`,
                    heading: HeadingLevel.HEADING_2,
                    spacing: { before: 400, after: 200 },
                  }),
                  new Paragraph({
                    text: "Student's Answer:",
                    heading: HeadingLevel.HEADING_3,
                    spacing: { before: 200, after: 100 },
                  }),
                  ...(ans.answer
                    ? ans.answer.split('\n').map((line) => new Paragraph({ text: line, spacing: { after: 80 } }))
                    : [new Paragraph({ text: '(No answer provided)', spacing: { after: 200 } })]),
                ];

                if (ans.answer) {
                  const wordCount = ans.answer.split(/\s+/).filter(Boolean).length;
                  taskParagraphs.push(
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: `Word count: ${wordCount}`,
                          italics: true,
                          size: 20,
                        }),
                      ],
                      spacing: { after: 300 },
                    })
                  );
                }

                if (ans.instructorFeedback) {
                  taskParagraphs.push(
                    new Paragraph({
                      text: 'Instructor Feedback:',
                      heading: HeadingLevel.HEADING_3,
                      spacing: { before: 200, after: 100 },
                    }),
                    ...ans.instructorFeedback.split('\n').map((line) => new Paragraph({ text: line, spacing: { after: 80 } }))
                  );
                }

                if (ans.instructorAdvice) {
                  taskParagraphs.push(
                    new Paragraph({
                      text: 'Instructor Advice:',
                      heading: HeadingLevel.HEADING_3,
                      spacing: { before: 200, after: 100 },
                    }),
                    ...ans.instructorAdvice.split('\n').map((line) => new Paragraph({ text: line, spacing: { after: 80 } }))
                  );
                }

                return taskParagraphs;
              }),
            ],
          },
        ],
      });

      const blob = await Packer.toBlob(doc);
      saveAs(blob, `${studentName}_${examName}_WritingAnswers.docx`);
      showToast('Document exported successfully', 'success');
    } catch (error) {
      console.error('Failed to export document:', error);
      showToast('Failed to export document', 'error');
    }
  };

  // Render item for GroupedAccordionList
  const renderItem = (answer) => {
    const answeredCount = getAnsweredCount(answer.answers);
    const feedbackStatus = hasFeedback(answer.answers);

    return (
      <ListItem
        key={answer._id}
        sx={{
          borderTop: '1px solid',
          borderColor: 'divider',
          py: 2.5,
          px: 3,
          backgroundColor: 'background.paper',
        }}
      >
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {getStudentName(answer)}
              </Typography>
              <Chip
                label={`${answeredCount}/2`}
                color={answeredCount === 2 ? 'success' : 'default'}
                size="small"
                sx={{ height: 20, fontSize: '0.75rem' }}
              />
              {feedbackStatus ? (
                <Chip
                  label="Reviewed"
                  color="success"
                  size="small"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.75rem' }}
                />
              ) : (
                <Chip
                  label="Pending"
                  color="warning"
                  size="small"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.75rem' }}
                />
              )}
            </Box>
          }
          secondary={
            <Box>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {getStudentEmail(answer)}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Submitted: {format(new Date(answer.createdAt), 'dd-MM-yyyy HH:mm')}
              </Typography>
            </Box>
          }
        />
        <ListItemSecondaryAction>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Export to Word">
              <IconButton
                size="small"
                onClick={() => exportToWord(answer)}
                sx={{
                  color: 'success.main',
                  '&:hover': { backgroundColor: 'success.50' },
                }}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={isPlatformAdmin ? "View & Edit" : "View"}>
              <IconButton
                size="small"
                onClick={() => handleViewDetails(answer)}
                sx={{
                  color: 'primary.main',
                  '&:hover': { backgroundColor: 'primary.50' },
                }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {isPlatformAdmin && (
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  onClick={() => handleDelete(answer._id)}
                  sx={{
                    color: 'error.main',
                    '&:hover': { backgroundColor: 'error.50' },
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </ListItemSecondaryAction>
      </ListItem>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Writing Answers Management"
        subtitle="View, manage and provide feedback on user writing submissions"
        icon={<EditIcon />}
        iconColor="warning.main"
      />

      <StatsCards stats={stats} columns={3} />

      <GroupedAccordionList
        groups={groupsArray}
        itemsKey="answers"
        isGroupExpanded={isGroupExpanded}
        onToggleGroup={toggleGroup}
        renderItem={renderItem}
        itemLabel="student"
      />

      {/* Details Dialog - Kept as is for full functionality */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Writing Answer Details
            </Typography>
            {!editMode && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <ModernButton
                  startIcon={<DownloadIcon />}
                  onClick={() => exportToWord(selectedAnswer)}
                  variant="outlined"
                  size="small"
                  color="success"
                >
                  Export to Word
                </ModernButton>
                <ModernButton startIcon={<EditIcon />} onClick={handleEditFeedback} variant="contained" size="small">
                  Add/Edit Feedback
                </ModernButton>
              </Box>
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedAnswer && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                    Student Name
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {getStudentName(selectedAnswer)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                    Email
                  </Typography>
                  <Typography variant="body1">
                    {getStudentEmail(selectedAnswer)}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                    Exam
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedAnswer.userProductRegistrationId?.productId?.name || selectedAnswer.userProductRegistrationId?.productId?.label || 'N/A'}
                  </Typography>
                  {selectedAnswer.userProductRegistrationId?.productId?.startDate && (
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {format(new Date(selectedAnswer.userProductRegistrationId.productId.startDate), 'dd-MM-yyyy HH:mm')}
                    </Typography>
                  )}
                </Grid>
              </Grid>

              {selectedAnswer.answers.map((ans, idx) => (
                <Card key={idx} sx={{ mb: 3, borderRadius: 2, border: 1, borderColor: 'grey.300' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                      Task {ans.questionNumber}
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary', fontWeight: 600 }}>
                      Student's Answer
                    </Typography>
                    <Paper sx={{ p: 2, backgroundColor: 'grey.50', mb: 3 }}>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.9rem' }}>
                        {ans.answer || '(No answer provided)'}
                      </Typography>
                      {ans.answer && (
                        <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1, display: 'block' }}>
                          Word count: {ans.answer.split(/\s+/).filter(Boolean).length}
                        </Typography>
                      )}
                    </Paper>

                    {editMode ? (
                      <>
                        <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary', fontWeight: 600 }}>
                          Instructor Feedback
                        </Typography>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          value={editedFeedback[ans.questionNumber]?.instructorFeedback || ''}
                          onChange={(e) => handleFeedbackChange(ans.questionNumber, 'instructorFeedback', e.target.value)}
                          placeholder="Provide detailed feedback on the writing..."
                          sx={{ mb: 2 }}
                        />

                        <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary', fontWeight: 600 }}>
                          Instructor Advice
                        </Typography>
                        <TextField
                          fullWidth
                          multiline
                          rows={2}
                          value={editedFeedback[ans.questionNumber]?.instructorAdvice || ''}
                          onChange={(e) => handleFeedbackChange(ans.questionNumber, 'instructorAdvice', e.target.value)}
                          placeholder="Provide improvement suggestions and advice..."
                        />
                      </>
                    ) : (
                      <>
                        {ans.instructorFeedback && (
                          <>
                            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary', fontWeight: 600 }}>
                              Instructor Feedback
                            </Typography>
                            <Paper sx={{ p: 2, backgroundColor: 'success.50', mb: 2, border: 1, borderColor: 'success.main' }}>
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                {ans.instructorFeedback}
                              </Typography>
                            </Paper>
                          </>
                        )}

                        {ans.instructorAdvice && (
                          <>
                            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary', fontWeight: 600 }}>
                              Instructor Advice
                            </Typography>
                            <Paper sx={{ p: 2, backgroundColor: 'info.50', border: 1, borderColor: 'info.main' }}>
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                {ans.instructorAdvice}
                              </Typography>
                            </Paper>
                          </>
                        )}

                        {!ans.instructorFeedback && !ans.instructorAdvice && (
                          <Paper sx={{ p: 2, backgroundColor: 'warning.50', border: 1, borderColor: 'warning.main' }}>
                            <Typography variant="body2" sx={{ color: 'warning.dark' }}>
                              No instructor feedback provided yet. Click "Add/Edit Feedback" to provide feedback.
                            </Typography>
                          </Paper>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {editMode ? (
            <>
              <ModernButton onClick={() => setEditMode(false)} variant="outlined" startIcon={<CloseIcon />}>
                Cancel
              </ModernButton>
              <ModernButton onClick={handleSaveFeedback} variant="contained" startIcon={<SaveIcon />}>
                Save Feedback
              </ModernButton>
            </>
          ) : (
            <ModernButton onClick={() => setDialogOpen(false)} variant="outlined">
              Close
            </ModernButton>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WritingAnswersManagement;
